import lowerCase from 'lodash/lowerCase';
import upperFirst from 'lodash/upperFirst';
import * as React from 'react';
import { Link } from 'react-router-dom';
import toNumber from 'lodash/toNumber';
import sumBy from 'lodash/sumBy';
import { CopyableText, FormattedCurrency, getEntitlementRouteFromLineItem } from '@css-xp/base';
import { SourceSystem } from '@atlassian/cssxp-typescommon';
import { Adjustment, BillingDocWithLineItems, Refund, RefundItem } from '@atlassian/cssxp-types-billing';
import { startCase, upperCase } from 'lodash';
// import { isFinancePerson } from '../../../common/security/selectors';

export const RefundLineItems = (billingDoc: BillingDocWithLineItems) => {
  if (!billingDoc) {
    return null;
  }

  const {
    currency,
    totalTax,
    totalExTax,
    total,
    refundItems,
    reason,
    reasonCode,
    creationNotes,
    sourceSystem,
  } = billingDoc as Refund;

  const adjustedAmount = (subtotal: string | null | undefined, discount: Adjustment[] | null | undefined): number => {
    const actualSubtotal = toNumber(subtotal);
    const actualDiscount = sumBy(discount, 'amount');
    if (!isNaN(actualSubtotal) && actualSubtotal !== 0 && !isNaN(actualDiscount)) {
      return actualSubtotal - actualDiscount;
    }

    return 0;
  };

  const isCCPRefund = upperCase(sourceSystem || '') === SourceSystem.CCP;

  return (
    <>
      <table className="aui billing-line-items" data-testid="refund-line-items-table">
        <colgroup>
          <col className="description-column" />
          <col className="billing-rate-column" />

          {isCCPRefund && (
            <>
              <col className="billing-rate-column" />
              <col className="billing-rate-column" />
            </>
          )}

          <col className="amount-column" />
        </colgroup>
        <thead>
          <tr>
            <th>Details</th>
            <th>Tax Rate %</th>
            {isCCPRefund && (
              <>
                <th className="amount-cell">Unit Price</th>
                <th className="amount-cell">Adjustment(s)</th>
              </>
            )}
            <th className="amount-cell">Amount</th>
          </tr>
        </thead>
        <tbody>
          {refundItems.map((item: RefundItem) => (
            <React.Fragment key={item.id}>
              <tr className="line-item">
                <td>
                  {item?.entitlementNumber && (
                    <div>
                      <Link
                        to={
                          //@ts-ignore
                          getEntitlementRouteFromLineItem(item)
                        }
                      >
                        <strong>{item?.entitlementNumber}</strong>
                      </Link>
                    </div>
                  )}
                  {
                    <div>
                      <CopyableText text={item?.entitlementId} />
                    </div>
                  }
                  <div>
                    {startCase(item.description ?? '')}
                    {item?.quantity && Number(item?.quantity) > 0 && `${' - '}${item?.quantity} units`}
                  </div>
                </td>
                <td>{item.taxRate}</td>
                {isCCPRefund && (
                  <>
                    <td className="amount-cell">
                      <FormattedCurrency value={item.totalExTax ?? 0} currency={currency} />
                    </td>
                    <td className="amount-cell">
                      {(item?.adjustments ?? []).length > 0 ? (
                        (item?.adjustments ?? []).map((item, index) => (
                          <div key={index}>
                            <FormattedCurrency value={item?.amount ?? 0} currency={currency} />
                          </div>
                        ))
                      ) : (
                        <FormattedCurrency value={0} currency={currency} />
                      )}
                    </td>
                  </>
                )}
                <td className="amount-cell">
                  <FormattedCurrency value={adjustedAmount(item?.totalExTax, item?.adjustments)} currency={currency} />
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <table className="billing-total-table" data-testid="refund-total-table">
        <tbody>
          <tr>
            <td>Total excl. tax</td>
            <td>
              <FormattedCurrency value={totalExTax ?? 0} currency={currency} />
            </td>
          </tr>
          <tr>
            <td>Tax</td>
            <td>
              <FormattedCurrency value={totalTax ?? 0} currency={currency} />
            </td>
          </tr>
          <tr className="billing-grand-total">
            <td>Total</td>
            <td>
              <FormattedCurrency value={total ?? 0} currency={currency} showCurrencyCode />
            </td>
          </tr>
        </tbody>
      </table>
      <div className="billing-doc-notes">
        <h4>Refund Summary</h4>
        <table className="aui refund-summary-items" data-test-id="refund-summary">
          <colgroup>
            <col className="description-column" />
            <col className="reasons-column" />
          </colgroup>
          <thead>
            <tr>
              <th>Reason</th>
              {sourceSystem === SourceSystem.HAMS && <th>Reason code</th>}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{upperFirst(lowerCase(reason))}</td>
              {sourceSystem === SourceSystem.HAMS && <td>{upperFirst(lowerCase(reasonCode))}</td>}
            </tr>
          </tbody>
        </table>
        {creationNotes && (
          <div className="refund-creation-notes">
            <h4>Notes</h4>
            <p>{creationNotes}</p>
          </div>
        )}
      </div>
    </>
  );
};
